import { purple } from '@mui/material/colors';
import { PointLayer, PointLayerProps } from '@sofarocean/mapbox-context';
import { subMinutes } from 'date-fns';
import { compact, isNil } from 'lodash';
import { LayoutSpecification, PaintSpecification } from 'mapbox-gl';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useSoundPressureReadings } from '@/api/historicalData';
import { EnrichedMapDevice } from '@/components/map/SpotterMap';
import { SPL_READING_WINDOW_MINUTES } from '@/config';
import { useMapUiContext } from '@/context/MapUiContext';
import { useTimelineNavigationContext } from '@/context/TimelineNavigationContext';
import { useAnimation } from '@/hooks/useAnimation';
import { useNow } from '@/hooks/useNow';
import { useStableValue } from '@/hooks/useStableValue';
import { isFiniteNumber } from '@/utils';

const EMPTY_LAYOUT: LayoutSpecification = {};

const SPL_THRESHOLDS = [110, 130, 190];

export const SplHalos = ({ beforeLayer, devices }: { beforeLayer?: string; devices: EnrichedMapDevice[] }) => {
    const spotterIds = useMemo(() => devices.map(({ spotterId }) => spotterId), [devices]);

    const { baseLayer } = useMapUiContext();
    const { selectedTimestamp } = useTimelineNavigationContext();
    const now = useNow(300, { enabled: !selectedTimestamp });
    const targetTime = selectedTimestamp ?? now;

    /*
     * Not `useDateRangeForSample`, because `useSoundPressureReadings` expands this out to common hour
     * boundaries itself before it asks the endpoint for anything. This is the window it hands back, so
     * neighbouring cursor positions still share one query while the readings materialized are only the ones
     * a halo can be drawn from, which is what `points` below already searches within.
     * useSoundPressureData is the only hook on the /fetch/sensor-data path that does it's own date range expansion.
     */
    const targetMs = new Date(targetTime).getTime();
    const windowStart = subMinutes(targetMs, SPL_READING_WINDOW_MINUTES).getTime();
    const dateRange = useMemo(
        () => ({ startDate: new Date(windowStart), endDate: new Date(targetMs) }),
        [targetMs, windowStart]
    );

    const haloColor = baseLayer === 'satellite' ? purple[100] : purple[700];

    const { data: sensorDataBySpotter } = useSoundPressureReadings({
        spotterIds,
        dateRange,
    });

    const deviceLocations = useStableValue(
        useMemo(
            () =>
                devices.map((device) => {
                    const { latitude, longitude } = device.location ?? device.track?.[0] ?? {};
                    if (isNil(latitude) || isNil(longitude)) {
                        return null;
                    }
                    return {
                        id: device.spotterId,
                        latitude,
                        longitude,
                    };
                }),
            [devices]
        )
    );

    const points = useMemo(() => {
        if (!sensorDataBySpotter) return [];

        // not using targetTime/now because if we're looking for latest, we're fine accepting something more recent than the hook's last update
        const targetTs = selectedTimestamp ? new Date(selectedTimestamp).getTime() : undefined;

        return compact(
            deviceLocations.map((device, i) => {
                const sensorData = sensorDataBySpotter[i];
                if (!device || !sensorData) {
                    return null;
                }

                let bestTimestampMs = -Infinity;
                let bestSpl: number | null = null;

                for (const readings of Object.values(sensorData)) {
                    // searching backwards because with live data it's much more likely near the end,
                    // and that's the hardest case with frequent updates.
                    // for non-live on average it's near the middle, so it doesn't matter either way.
                    for (let i = readings.length - 1; i >= 0; i--) {
                        const reading = readings[i];
                        const timestampMs = Date.parse(reading.timestamp);
                        if (timestampMs <= windowStart) {
                            // too far back in time before our target (or now),
                            // stop looking
                            break;
                        }
                        if (targetTs && timestampMs >= targetTs) {
                            // still later than the target, keep looking
                            continue;
                        }
                        if (!isFiniteNumber(reading.broadbandSpl)) {
                            // not a real reading, keep looking
                            continue;
                        }
                        // now we're at the first reading we've found before the target,
                        // if it's more recent than we got from any other sensor, this is
                        // our reading; if not, move onto the next sensor if there is one.
                        if (timestampMs > bestTimestampMs) {
                            bestTimestampMs = timestampMs;
                            bestSpl = reading.broadbandSpl;
                        }
                        break;
                    }
                }
                if (!bestSpl) {
                    return null;
                }
                return {
                    ...device,
                    properties: { spl: bestSpl },
                };
            })
        );
    }, [deviceLocations, sensorDataBySpotter, selectedTimestamp, windowStart]);

    const haloPaint = useMemo(
        () =>
            ({
                'circle-color': haloColor,
                'circle-blur': 0.9,
                'circle-opacity': [
                    'interpolate',
                    ['linear'],
                    ['get', 'spl'],
                    0,
                    0,
                    0.01,
                    0.1,
                    SPL_THRESHOLDS[0],
                    0.4,
                    SPL_THRESHOLDS[1],
                    0.6,
                    SPL_THRESHOLDS[2],
                    1,
                ],
                'circle-radius': ['interpolate', ['exponential', 2], ['zoom'], 2, 24, 5, 60],
            }) as PaintSpecification,
        [haloColor]
    );

    const ringPaint = useMemo(
        () =>
            ({
                'circle-opacity': 0,
                'circle-stroke-color': haloColor,
                'circle-stroke-width': 1,
                'circle-stroke-opacity': ['interpolate', ['linear'], ['get', 'radius'], 0, 0.5, 0.8, 0.5, 1, 0],
                /* really want circle-radius to just be (haloPaintype DeviceMapLocation, t['circle-radius'] * radius) but the interpolate has to be top-level, so hard to actually share */
                'circle-radius': [
                    'interpolate',
                    ['exponential', 2],
                    ['zoom'],
                    2,
                    ['*', 24, ['get', 'radius']],
                    5,
                    ['*', 60, ['get', 'radius']],
                ],
            }) as PaintSpecification,
        [haloColor]
    );

    const [ringPoints, setRingPoints] = useState<PointLayerProps['points']>([]);

    useEffect(
        () =>
            setRingPoints((oldPoints) =>
                points.flatMap((point) => {
                    const { spl } = point.properties;
                    if (!spl) return [];
                    let targetRingCount;
                    if (spl < SPL_THRESHOLDS[0]) {
                        targetRingCount = 1;
                    } else if (point.properties.spl < SPL_THRESHOLDS[1]) {
                        targetRingCount = 2;
                    } else {
                        targetRingCount = 3;
                    }
                    const oldRingPoints = oldPoints.filter((p) => p.id === point.id);
                    if (oldRingPoints.length === targetRingCount) {
                        // if this spotter's number of rings haven't changed, reuse the old radii
                        // so they don't reset every time any other spotter's data changes
                        return oldRingPoints.map((p) => ({
                            ...point,
                            properties: { ...point.properties, radius: p.properties!.radius },
                        }));
                    }
                    if (targetRingCount === 1) {
                        return [{ ...point, properties: { spl, radius: 0 } }];
                    }
                    if (targetRingCount === 2) {
                        return [
                            { ...point, properties: { spl, radius: 0 } },
                            { ...point, properties: { spl, radius: 0.5 } },
                        ];
                    }
                    return [
                        { ...point, properties: { spl, radius: 0 } },
                        { ...point, properties: { spl, radius: 1 / 3 } },
                        { ...point, properties: { spl, radius: 2 / 3 } },
                    ];
                })
            ),
        [points]
    );

    const previousFrameMs = useRef<number>();

    useAnimation(
        (timestampMs) => {
            // Cap elapsedMs so that if we were frozen/unfocused for a long time we don't make a huge jump
            // when we come back
            const elapsedMs = Math.min(timestampMs - (previousFrameMs.current ?? timestampMs), 100);
            previousFrameMs.current = timestampMs;

            setRingPoints((value) =>
                value.map((point) => {
                    const { spl, radius } = point.properties!;
                    const speed =
                        spl < SPL_THRESHOLDS[0]
                            ? spl / SPL_THRESHOLDS[0]
                            : spl < SPL_THRESHOLDS[1]
                              ? spl / SPL_THRESHOLDS[1]
                              : spl / SPL_THRESHOLDS[2];

                    // 3000 is determined by vibe: speed feels about right for SPLs we tested on
                    // it's ~ ms for a ring to complete one full expansion
                    const newRadius = radius + (speed * elapsedMs) / 3000;
                    point.properties!.radius = newRadius > 1 ? 0 : newRadius;
                    return point;
                })
            );
        },
        { enabled: ringPoints.length > 0 }
    );

    return (
        <>
            <PointLayer
                type="circle"
                style={{ layout: EMPTY_LAYOUT, paint: ringPaint }}
                points={ringPoints}
                beforeLayer={beforeLayer}
            />
            <PointLayer
                type="circle"
                style={{ layout: EMPTY_LAYOUT, paint: haloPaint }}
                points={points}
                beforeLayer={beforeLayer}
            />
        </>
    );
};
